import { useMemo } from "react";
import { useTranslation } from "@repo/core";
import { usePortfolioConstituents } from "./usePortfolioConstituents";
import {
  useAggregatedReturnsData,
  type DrawdownData,
} from "@repo/api";

// Type for the transformed matrix structure
export interface ReturnsMatrix {
  [year: number]: Record<string, number | null>;
}

export function useReturns() {
  const { tStrategy } = useTranslation();
  const portfolioConstituentsQuery = usePortfolioConstituents();
  const aggregatedReturnsQuery = useAggregatedReturnsData();
  const portfolioConstituents = useMemo(() => {
    if (portfolioConstituentsQuery.data) {
      return portfolioConstituentsQuery.data;
    } else {
      return {};
    }
  }, [portfolioConstituentsQuery.data]);
  const aggregatedReturns = useMemo(() => {
    if (aggregatedReturnsQuery.data) {
      return aggregatedReturnsQuery.data;
    } else {
      return {} as DrawdownData;
    }
  }, [aggregatedReturnsQuery.data]);

  const returnsSet = [
    ...Object.values(portfolioConstituents).map(
      (constituent) => constituent.Strategy
    ),
    ...Object.values(aggregatedReturns.Strategy || {}),
  ];
  const matrixData = {
    aggregations: aggregatedReturns.Strategy,
    matrix: Object.keys(portfolioConstituents).reduce((acc, date) => {
      return { ...acc, [date]: portfolioConstituents[date].Strategy };
    }, {}),
  };
  const yearsSet = useMemo(() => {
    return new Set(
      Object.keys(portfolioConstituents).map((d) => new Date(d).getFullYear())
    );
  }, [portfolioConstituents]);

  // Transform matrixData into a matrix with years as rows and months + YTD as columns
  const transformedMatrix: ReturnsMatrix = useMemo(() => {
    const matrix: ReturnsMatrix = {};
    const monthNames = [
      tStrategy("months.jan"),
      tStrategy("months.feb"),
      tStrategy("months.mar"),
      tStrategy("months.apr"),
      tStrategy("months.may"),
      tStrategy("months.jun"),
      tStrategy("months.jul"),
      tStrategy("months.aug"),
      tStrategy("months.sep"),
      tStrategy("months.oct"),
      tStrategy("months.nov"),
      tStrategy("months.dec"),
    ];

    // Initialize matrix for each year
    Array.from(yearsSet).forEach((year) => {
      matrix[year] = {};
      // Initialize all months to null
      monthNames.forEach((month) => {
        matrix[year][month] = null;
      });
      // Initialize YTD to null
      matrix[year]["YTD"] = null;
    });

    // Populate monthly returns from matrixData.matrix
    Object.entries(matrixData.matrix).forEach(([dateString, returnValue]) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = monthNames[date.getMonth()];

      if (matrix[year]) {
        matrix[year][month] =
          typeof returnValue === "number" ? returnValue * 100 : null;
      }
    });

    // Populate YTD values from matrixData.aggregations
    if (matrixData.aggregations) {
      // Group dates by year and find the latest date for each year
      const yearEndDates: Record<number, { date: Date; value: number }> = {};

      Object.entries(matrixData.aggregations).forEach(
        ([dateString, returnValue]) => {
          const date = new Date(dateString);
          const year = date.getFullYear();

          if (typeof returnValue === "number") {
            // Keep track of the latest date for each year
            if (!yearEndDates[year] || date > yearEndDates[year].date) {
              yearEndDates[year] = { date, value: returnValue * 100 };
            }
          }
        }
      );

      // Assign the latest value for each year as YTD
      Object.entries(yearEndDates).forEach(([yearString, { value }]) => {
        const year = parseInt(yearString);
        if (matrix[year]) {
          matrix[year]["YTD"] = value;
        }
      });
    }

    return matrix;
  }, [matrixData.matrix, matrixData.aggregations, yearsSet, tStrategy]);

  // Helper data for easier consumption
  const columnHeaders = [
    tStrategy("months.jan"),
    tStrategy("months.feb"),
    tStrategy("months.mar"),
    tStrategy("months.apr"),
    tStrategy("months.may"),
    tStrategy("months.jun"),
    tStrategy("months.jul"),
    tStrategy("months.aug"),
    tStrategy("months.sep"),
    tStrategy("months.oct"),
    tStrategy("months.nov"),
    tStrategy("months.dec"),
    "YTD",
  ];

  const sortedYears = useMemo(() => {
    return Array.from(yearsSet).sort((a, b) => a - b); // Sort descending (newest first)
  }, [yearsSet]);

  console.log("Matrix Data:", matrixData);
  console.log("Transformed Matrix:", transformedMatrix);

  // Helper function to detect empty data
  const isEmptyData = (data: unknown): boolean => {
    if (!data) return true;
    if (Array.isArray(data) && data.length === 0) return true;
    if (typeof data === "object" && Object.keys(data).length === 0) return true;
    return false;
  };

  const isPortfolioDataEmpty = isEmptyData(portfolioConstituentsQuery.data);
  const isAggregatedDataEmpty = isEmptyData(aggregatedReturnsQuery.data);

  const isError =
    portfolioConstituentsQuery.isError || aggregatedReturnsQuery.isError;

  return {
    matrixData,
    transformedMatrix,
    yearsSet,
    sortedYears,
    columnHeaders,
    returnsSet,
    // Empty data counts as "still loading" only while the queries can still
    // deliver. Once one of them has failed, empty means failed - otherwise the
    // consumer renders a spinner that never resolves.
    isLoading:
      !isError &&
      (portfolioConstituentsQuery.isLoading ||
        aggregatedReturnsQuery.isLoading ||
        isPortfolioDataEmpty ||
        isAggregatedDataEmpty),
    isError,
  };
}
